import React, { useEffect, useState } from "react";
import AdminHeader from "../common/AdminHeader";
import Footer from "../common/Footer";
import axios from "axios";
import Swal from "sweetalert2";
import { baseUrl } from "../../Utility/constants";

const AdminOperatorDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/Admin/GetAdminDashboardData?id=${localStorage.getItem(
          "userID"
        )}`
      );

      setDashboardData(response.data);

      // Greeting Popup (Once per Session)
      if (!sessionStorage.getItem("dashboardGreeting")) {
        Swal.fire({
          title: `Hi! ${localStorage.getItem("loggedUserFullName")} 👋`,
          text: "Have a great day!",
          icon: "success",
          timer: 5000,
          showConfirmButton: false,
        });

        sessionStorage.setItem("dashboardGreeting", "true");
      }
    } catch (err) {
      setError("Failed to fetch dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <AdminHeader />
        <div className="container mt-5 text-center">
          <div className="spinner-border text-primary"></div>
          <h5 className="mt-3">Loading Dashboard...</h5>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <AdminHeader />
        <div className="container mt-5">
          <div className="alert alert-danger">{error}</div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <AdminHeader />

      <div className="container mt-4 mb-5">

        <div className="text-center">
          <h2 className="fw-bold">
            {localStorage.getItem("Role") === "SUPERADMIN"
              ? "Super Admin Dashboard"
              : "Admin Dashboard"}
          </h2>

          <p className="text-muted">
            Welcome <strong>{localStorage.getItem("loggedUserFullName")}</strong>
          </p>
        </div>

        <div className="row">

          {/* Academic Information */}

          <div className="col-lg-6">
            <div className="card shadow border-0 rounded-4 h-100">
              <div className="card-header bg-primary text-white fw-bold">
                <i className="bi bi-mortarboard-fill me-2"></i>
                Academic Information
              </div>

              <div className="card-body">

                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Total Classes</span>
                  <span className="badge bg-primary fs-6">
                    {dashboardData.classCount}
                  </span>
                </div>

                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Total Sections</span>
                  <span className="badge bg-info fs-6">
                    {dashboardData.sectionCount}
                  </span>
                </div>

                <div className="d-flex justify-content-between py-2">
                  <span>Total Streams</span>
                  <span className="badge bg-success fs-6">
                    {dashboardData.streamCount}
                  </span>
                </div>

              </div>
            </div>
          </div>

          {/* Student Information */}

          <div className="col-lg-6">
            <div className="card shadow border-0 rounded-4 h-100">
              <div className="card-header bg-success text-white fw-bold">
                <i className="bi bi-people-fill me-2"></i>
                Student Information
              </div>

              <div className="card-body">

                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Total Registered</span>
                  <span className="badge bg-primary fs-6">
                    {dashboardData.studentCount}
                  </span>
                </div>

                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Approved</span>
                  <span className="badge bg-success fs-6">
                    {dashboardData.approvedStudentCount}
                  </span>
                </div>

                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Pending</span>
                  <span className="badge bg-warning text-dark fs-6">
                    {dashboardData.pendingStudentCount}
                  </span>
                </div>

                <div className="d-flex justify-content-between py-2">
                  <span>Rejected</span>
                  <span className="badge bg-danger fs-6">
                    {dashboardData.rejectedStudentCount}
                  </span>
                </div>

              </div>
            </div>
          </div>
        </div>

        <div className="row mt-2">
          {/* Grant Type Student Distribution */}
          <div className="col-lg-12 mt-4">
            <div className="card shadow border-0 rounded-4">
              <div className="card-header bg-info text-white fw-bold">
                <i className="bi bi-award-fill me-2"></i>
                Grant Type Student Distribution
              </div>

              <div className="card-body py-3">
                <div className="row g-3">
                  {dashboardData.grantTypeWiseCount?.map((grant) => {
                    const grantNames = {
                      1: "OPEN",
                      2: "OBC",
                      3: "SC",
                      4: "ST",
                      5: "SBC",
                      6: "EWS",
                      7: "NT",
                    };

                    const badgeColors = {
                      1: "primary",
                      2: "success",
                      3: "danger",
                      4: "dark",
                      5: "warning",
                      6: "info",
                      7: "secondary",
                    };

                    return (
                      <div
                        key={grant.grantTypeID}
                        className="col-xl-3 col-lg-4 col-md-6 col-6"
                      >
                        <div className="border rounded-3 px-3 py-2 d-flex justify-content-between align-items-center bg-light h-100">
                          <div>
                            <div className="fw-semibold text-dark">
                              {grant.grantTypeName}
                            </div>
                            {/* <small className="text-muted">
                              Grant ID: {grant.grantTypeID}
                            </small> */}
                          </div>

                          <span
                            className={`badge bg-${badgeColors[grant.grantTypeID]} rounded-pill fs-6 px-3 py-2`}
                          >
                            {grant.studentCount}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mt-2 g-2">

          {/* Academic Student Distribution */}

          <div className="col-lg-12 mt-2">
            <div className="card shadow border-0 rounded-4">
              <div className="card-header bg-secondary text-white fw-bold">
                <i className="bi bi-bar-chart-line-fill me-2"></i>
                Academic Student Distribution
              </div>

              <div className="card-body">

                <div className="row">

                  {/* Class 1-10 */}
                  <div className="col-md-4 border-end">
                    <h5 className="text-primary fw-bold mb-3">
                      <i className="bi bi-book-fill me-2"></i>
                      Class 1 - 10
                    </h5>

                    {dashboardData.class1To10StudentCount?.map((item) => (
                      <div
                        key={item.classId}
                        className="d-flex justify-content-between align-items-center py-2 border-bottom"
                      >
                        <span>Class {item.className}</span>
                        <span className="badge bg-primary rounded-pill px-3">
                          {item.studentCount}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Class 11-12 Stream Wise */}
                  <div className="col-md-4 border-end">
                    <h5 className="text-success fw-bold mb-3">
                      <i className="bi bi-diagram-3-fill me-2"></i>
                      Stream Wise (11-12)
                    </h5>

                    {dashboardData.class11And12StreamCount?.map((cls) => (
                      <div key={cls.className} className="mb-3">

                        <div className="fw-semibold text-success mb-2">
                          Class {cls.className}
                        </div>

                        {cls.streams.map((stream, index) => (
                          <div
                            key={index}
                            className="d-flex justify-content-between py-1 border-bottom"
                          >
                            <span>{stream.streamName}</span>
                            <span className="badge bg-success rounded-pill">
                              {stream.studentCount}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>

                  {/* Class 11-12 Section Wise */}
                  <div className="col-md-4">
                    <h5 className="text-warning fw-bold mb-3">
                      <i className="bi bi-grid-3x3-gap-fill me-2"></i>
                      Section Wise (11-12)
                    </h5>

                    {dashboardData.class11And12SectionCount?.map((cls) => (
                      <div key={cls.className} className="mb-3">

                        <div className="fw-semibold text-warning mb-2">
                          Class {cls.className}
                        </div>

                        {cls.sections.map((section, index) => (
                          <div
                            key={index}
                            className="d-flex justify-content-between py-1 border-bottom"
                          >
                            <span>Section {section.sectionName}</span>
                            <span className="badge bg-warning text-dark rounded-pill">
                             {section.studentCount} / {section.intake}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>

                </div>

              </div>
            </div>
          </div>
        </div>

        <div className="row mt-2 g-2">
          {/* User Information */}

          <div className="col-lg-6">
            <div className="card shadow border-0 rounded-4 h-100">
              <div className="card-header bg-dark text-white fw-bold">
                <i className="bi bi-person-workspace me-2"></i>
                User Information
              </div>

              <div className="card-body">

                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Total Users</span>
                  <span className="badge bg-primary fs-6">
                    {dashboardData.usersCount}
                  </span>
                </div>
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Total Admins</span>
                  <span className="badge bg-warning text-dark fs-6">
                    {dashboardData.adminCount}
                  </span>
                </div>

                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Total Teachers</span>
                  <span className="badge bg-success fs-6">
                    {dashboardData.teacherCount}
                  </span>
                </div>

                <div className="ps-3">

                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span>
                      <i className="bi bi-arrow-return-right me-2"></i>
                      Class Teachers
                    </span>
                    <span className="badge bg-info fs-6">
                      {dashboardData.classTeacherCount}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span>
                      <i className="bi bi-arrow-return-right me-2"></i>
                      Subject Teachers
                    </span>
                    <span className="badge bg-secondary fs-6">
                      {dashboardData.subjectTeacherCount}
                    </span>
                  </div>

                </div>

                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Exam Heads</span>
                  <span className="badge bg-warning text-dark fs-6">
                    {dashboardData.examheadCount}
                  </span>
                </div>

                <div className="d-flex justify-content-between py-2">
                  <span>Accountants</span>
                  <span className="badge bg-danger fs-6">
                    {dashboardData.accountantCount}
                  </span>
                </div>

              </div>
            </div>
          </div>

          {/* Leaving Certificate */}

          <div className="col-lg-6">
            <div className="card shadow border-0 rounded-4 h-100">
              <div className="card-header bg-danger text-white fw-bold">
                <i className="bi bi-file-earmark-text-fill me-2"></i>
                Leaving Certificate Information
              </div>

              <div className="card-body">

                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Total Certificates</span>
                  <span className="badge bg-primary fs-6">
                    {dashboardData.lcCount}
                  </span>
                </div>

                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Original Certificates</span>
                  <span className="badge bg-success fs-6">
                    {dashboardData.lcOriginalCount}
                  </span>
                </div>

                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span>Duplicate Certificates</span>
                  <span className="badge bg-warning text-dark fs-6">
                    {dashboardData.lcDuplicateCount}
                  </span>
                </div>

                <div className="d-flex justify-content-between py-2">
                  <span>Private Certificates</span>
                  <span className="badge bg-danger fs-6">
                    {dashboardData.lcPrivateCount}
                  </span>
                </div>

              </div>
            </div>
          </div>

        </div>




      </div>

      <Footer />
    </>
  );
};

export default AdminOperatorDashboard;